import genericMemo from "Common/componentUtils/genericMemo";
import getShorterText from "Common/componentUtils/getShorterText";
import getHighlighted from "Common/componentUtils/highlight";
import isBase64Image from "Common/componentUtils/isBase64Image";
import { ROUTE_ANCHOR } from "Common/routingUtils";
import RotatedArrow from "Components/rotatedArrow";
import classNames from "classnames";
import compact from "lodash/compact";
import isEmpty from "lodash/isEmpty";
import join from "lodash/join";
import map from "lodash/map";
import trimStart from "lodash/trimStart";
import React, { useCallback, useMemo, useRef } from "react";
import { Draggable } from "react-drag-and-drop";
import { NAV_ITEM_ACTIVE_CLASS_NAME, NAV_ITEM_CLASS_NAME, NAV_ITEM_DISABLED_CLASS_NAME, NAV_ITEM_HAS_SUB_ITEMS_CLASS_NAME, NAV_ITEM_SELECTED_CLASS_NAME } from "../constants";
import Styled, { SIZES } from "./sidebarMenuItem.styled";
import { onDragEnd, onDragStart } from "./utils/dragging";
const handleStopEvent = (e) => {
    e.preventDefault();
    e.stopPropagation();
};
const isImgSrc = (icon) => /\./.test(icon) || isBase64Image(icon);
const hasDisabledClassRegexp = new RegExp(`(^|\\s)${NAV_ITEM_DISABLED_CLASS_NAME}(\\s|$)`);
function SidebarMenuItem(props) {
    const ref = useRef(null);
    const hasSubItems = Boolean(props.subItems);
    const handleOpenedChanged = useCallback(() => {
        if (!props.opened && props.onSubmenuShown) {
            props.onSubmenuShown(props.data);
        }
        props.onOpenedChanged && props.onOpenedChanged(props.data.key, !props.opened);
    }, [props.onOpenedChanged, props.onSubmenuShown, props.data.key, props.opened]);
    const icons = useMemo(() => compact([
        props.data.useDotIcon &&
            classNames("dx-icon dx-icon-isnotblank text-primary-500 v-align-middle", !props.data.selected && "invisible", props.data.icon ? "font-small-25" : "font-small-35"),
        props.data.icon && !isImgSrc(props.data.icon)
            ? classNames(props.data.icon, !/(^| )font-h\d($| )/.test(props.data.icon) && "font-h5", "v-align-middle", props.active && "navbar-icon-active", props.data.useDotIcon && "ml-0_25")
            : props.data.icon
    ]), [props.data.icon, props.data.useDotIcon, props.selected, props.active]);
    const hasMenuLinkActionsPanel = Boolean(props.data.actionElements);
    const href = props.data.hash
        ? `${ROUTE_ANCHOR}${trimStart(props.data.hash, ROUTE_ANCHOR)}`
        : undefined;
    const Comp = href ? Styled.MenuLink : Styled.MenuBlock;
    const gridCols = useMemo(() => join(compact([
        hasSubItems && "auto",
        !isEmpty(icons) && `${props.data.iconDefaultWidth || (icons.length === 1 ? 20 : 26)}px`,
        "1fr",
        hasMenuLinkActionsPanel && "auto"
    ]), " ") || "1fr", [icons, props.data.iconDefaultWidth, hasSubItems, hasMenuLinkActionsPanel]);
    const handleClick = useCallback((e) => {
        !props.data.hash && e.preventDefault();
        e.stopPropagation();
        if (props.data.draggableType)
            return;
        props.onItemClick && props.onItemClick(props.data, e);
        if (props.subItems)
            handleOpenedChanged();
        else
            props.onSelectChanged && props.onSelectChanged(props.data.key);
    }, [
        props.data.key,
        props.data.hash,
        props.data.draggableType,
        props.subItems,
        props.onItemClick,
        props.onSelectChanged,
        handleOpenedChanged
    ]);
    const handleDoubleClick = useCallback((e) => {
        e.stopPropagation();
        props.onItemDoubleClick && props.onItemDoubleClick(props.data, e);
    }, [props.onItemDoubleClick, props.data]);
    const elem = (React.createElement(Comp, { ref: ref, key: props.data.key, gridCols: gridCols, draggable: "false", className: classNames("overflow-hidden normal-wrap", !props.noStyled && [
            NAV_ITEM_CLASS_NAME,
            icons.length > 1 && "pl-0_25",
            hasSubItems && NAV_ITEM_HAS_SUB_ITEMS_CLASS_NAME,
            props.active && NAV_ITEM_ACTIVE_CLASS_NAME,
            !props.active && props.selected && NAV_ITEM_SELECTED_CLASS_NAME,
            props.margin && "mr-xxs ml-xxs",
            props.data.className
        ], props.className), "data-active": props.active, "data-selected": props.selected, href: href, onClick: handleClick, onDoubleClick: handleDoubleClick },
        hasSubItems && (React.createElement(Styled.MenuLinkActionsPanel, { position: "left", actionIconColor: props.actionIconColor, onClick: handleStopEvent },
            React.createElement(RotatedArrow, { className: "flex-box-centered-content text-light-on-surface-variant", open: props.opened, size: 10, onClick: handleOpenedChanged }))),
        !isEmpty(icons) && (React.createElement("div", { className: "text-left flex-box-centered-content line-height-1" }, map(icons, icon => !isImgSrc(icon) ? (React.createElement("i", { key: icon, className: icon })) : (React.createElement("img", { key: icon, src: icon, height: SIZES.IMG_SIZE, width: SIZES.IMG_SIZE, alt: "" }))))),
        React.createElement("div", { className: classNames("overflow-hidden", !isEmpty(icons) && "pl-0_5") },
            React.createElement("div", { className: "text-overflow-ellipsis break-word pr-0_5 pt-0_25 pb-0_25", title: props.data.title || "" }, getHighlighted(getShorterText(props.data.title, props.titleMaxLength), props.filterText)),
            props.data.subTitle && (React.createElement("div", { className: "text-muted font-smallest text-overflow-ellipsis" }, props.data.subTitle))),
        hasMenuLinkActionsPanel && (React.createElement(Styled.MenuLinkActionsPanel, { className: classNames(props.data.className &&
                hasDisabledClassRegexp.test(props.data.className) &&
                "no-pointer-events"), position: "right", actionIconColor: props.actionIconColor, onClick: handleStopEvent }, props.data.actionElements))));
    return (React.createElement(React.Fragment, null,
        props.data.draggableType ? (React.createElement(Draggable, { type: props.data.draggableType, scale: 1, data: props.data.draggableData, onDragStart: onDragStart, onDragEnd: onDragEnd }, elem)) : (elem),
        hasSubItems && (React.createElement(Styled.SlideUpDown, { key: `${props.data.key}_content`, open: Boolean(props.opened) }, map(props.subItems, item => (React.createElement(SidebarMenuItem, { data: item, key: item.key, selected: item.selected, active: item.active, opened: item.opened, subItems: item.subItems, depth: props.depth + 1, onOpenedChanged: !item.isBtn ? props.onOpenedChanged : undefined, onSelectChanged: !item.isBtn ? props.onSelectChanged : undefined, onItemClick: props.onItemClick, onItemDoubleClick: props.onItemDoubleClick, filterText: props.filterText, actionIconColor: props.actionIconColor, margin: props.margin })))))));
}
export default genericMemo(SidebarMenuItem);
SidebarMenuItem.defaultProps = {
    titleMaxLength: 60,
    actionIconColor: "gray-300"
};
